import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Building2, User, MapPin, Truck, CheckCircle2, ArrowLeft } from "lucide-react";
import api from "../lib/api";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
const CHANNELS = [
  { value: "Retail", label: "Retail / Normal Trade" },
  { value: "GDO", label: "GDO" },
  { value: "DO", label: "DO" },
  { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
  { value: "Distributore", label: "Distributore" },
];

export default function PublicRegistrationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sameAsLegal, setSameAsLegal] = useState(true);
  
  const [formData, setFormData] = useState({
    // Dati Azienda
    company_name: "",
    password: "", // Per accesso B2B
    vat_number: "",
    fiscal_code: "",
    sdi_code: "",
    pec: "",
    // Contatto principale
    contact_name: "",
    contact_email: "", // Email del contatto principale
    email: "", // Email per login
    phone: "",
    // Riferimenti Amministrativi
    admin_contact_name: "",
    admin_contact_email: "",
    admin_contact_phone: "",
    // Indirizzo sede
    address: "",
    city: "",
    region: "",
    postal_code: "",
    country: "Italia",
    // Consegna
    delivery_contact: "",
    delivery_address: "",
    delivery_city: "",
    delivery_postal_code: "",
    delivery_notes: "",
    // Default hidden fields
    channel: "Retail", // Default, will be reviewed by admin
    notes: "",
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLegalAddressChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (sameAsLegal) {
        // Auto-update delivery fields if checkbox is checked
        if (field === 'address') newData.delivery_address = value;
        if (field === 'city') newData.delivery_city = value;
        if (field === 'postal_code') newData.delivery_postal_code = value;
      }
      return newData;
    });
  };

  // Validation functions for each step
  const validateStep1 = () => {
    if (!formData.company_name.trim()) {
      toast.error("Inserisci la Ragione Sociale");
      return false;
    }
    if (!formData.vat_number.trim()) {
      toast.error("Inserisci la Partita IVA");
      return false;
    }
    if (!formData.sdi_code.trim()) {
      toast.error("Inserisci il Codice SDI");
      return false;
    }
    if (!formData.pec.trim()) {
      toast.error("Inserisci la PEC");
      return false;
    }
    // Simple PEC validation
    if (!formData.pec.includes("@")) {
      toast.error("Inserisci una PEC valida");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Address validation
    if (!formData.address.trim()) {
      toast.error("Inserisci l'indirizzo della sede legale");
      return false;
    }
    if (!formData.city.trim()) {
      toast.error("Inserisci la città");
      return false;
    }
    if (!formData.postal_code.trim()) {
      toast.error("Inserisci il CAP");
      return false;
    }
    // Login credentials validation
    if (!formData.email.trim()) {
      toast.error("Inserisci l'email per l'accesso");
      return false;
    }
    if (!formData.email.includes("@")) {
      toast.error("Inserisci un'email valida per l'accesso");
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      toast.error("La password deve essere di almeno 8 caratteri");
      return false;
    }
    // Contact validation
    if (!formData.contact_name.trim()) {
      toast.error("Inserisci il nome del contatto principale");
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error("Inserisci il telefono del contatto principale");
      return false;
    }
    return true;
  };

  const handleNextStep = (nextStep) => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(nextStep);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Prepare data
    const payload = {
      ...formData,
      delivery_address: sameAsLegal ? formData.address : formData.delivery_address,
      delivery_city: sameAsLegal ? formData.city : formData.delivery_city,
      delivery_postal_code: sameAsLegal ? formData.postal_code : formData.delivery_postal_code,
      delivery_contact: sameAsLegal ? formData.contact_name : formData.delivery_contact,
    };

    try {
      await api.post("/public/register-client", payload);
      setIsSubmitted(true);
      toast.success("Registrazione completata con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg text-center p-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Registrazione Ricevuta!</h2>
          <p className="text-muted-foreground mb-6">
            Grazie per esserti registrato. I nostri operatori verificheranno i tuoi dati e attiveranno il tuo account a breve.
            Riceverai una email di conferma.
          </p>
          <Button onClick={() => navigate("/login")} className="w-full">
            Torna al Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
             <Link to="/login" className="text-slate-500 hover:text-slate-700 transition-colors">
               <ArrowLeft className="w-6 h-6" />
             </Link>
             <h1 className="text-2xl font-bold text-slate-900">Registrazione - Lacinio.net</h1>
          </div>
          <div className="hidden md:flex gap-2">
            <div className={`h-2 w-16 rounded-full ${step >= 1 ? 'bg-accent' : 'bg-slate-200'}`} />
            <div className={`h-2 w-16 rounded-full ${step >= 2 ? 'bg-accent' : 'bg-slate-200'}`} />
            <div className={`h-2 w-16 rounded-full ${step >= 3 ? 'bg-accent' : 'bg-slate-200'}`} />
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Dati Aziendali */}
          <div className={step === 1 ? "block space-y-6" : "hidden"}>
            <Card>
              <CardHeader>
                <div className="space-y-2">
                  <Label>Canale di Vendita *</Label>
                  <Select value={formData.channel} onValueChange={(value) => handleChange("channel", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona canale" />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map((channel) => (
                        <SelectItem key={channel.value} value={channel.value}>{channel.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-accent" />
                  Dati Fiscali Azienda
                </CardTitle>
                <CardDescription>Inserisci i dati principali della tua attività</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Ragione Sociale *</Label>
                  <Input 
                    required 
                    placeholder="Es. Rossi SRL"
                    value={formData.company_name}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partita IVA *</Label>
                  <Input 
                    required 
                    placeholder="IT00000000000"
                    value={formData.vat_number}
                    onChange={(e) => handleChange("vat_number", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Codice Fiscale</Label>
                  <Input 
                    value={formData.fiscal_code}
                    onChange={(e) => handleChange("fiscal_code", e.target.value.toUpperCase())}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Codice SDI (Univoco) *</Label>
                  <Input 
                    required 
                    placeholder="XXXXXXX"
                    maxLength={7}
                    value={formData.sdi_code}
                    onChange={(e) => handleChange("sdi_code", e.target.value.toUpperCase())}
                  />
                </div>
                <div className="space-y-2">
                  <Label>PEC *</Label>
                  <Input 
                    required 
                    type="email"
                    placeholder="azienda@pec.it"
                    value={formData.pec}
                    onChange={(e) => handleChange("pec", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-end">
              <Button type="button" onClick={() => handleNextStep(2)}>
                Avanti
              </Button>
            </div>
          </div>

          {/* Step 2: Sede Legale e Contatti */}
          <div className={step === 2 ? "block space-y-6" : "hidden"}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  Sede Legale
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Indirizzo *</Label>
                  <Input 
                    required 
                    value={formData.address}
                    onChange={(e) => handleLegalAddressChange("address", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Città *</Label>
                  <Input 
                    required 
                    value={formData.city}
                    onChange={(e) => handleLegalAddressChange("city", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CAP *</Label>
                    <Input 
                      required 
                      maxLength={5}
                      value={formData.postal_code}
                      onChange={(e) => handleLegalAddressChange("postal_code", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input 
                      maxLength={2}
                      placeholder="RM"
                      value={formData.region}
                      onChange={(e) => handleChange("region", e.target.value.toUpperCase())}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Credenziali di Accesso (Login)
                </CardTitle>
                <CardDescription>
                  Dati per accedere al portale ordini B2B. L'email sarà il tuo nome utente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Email (Nome Utente) *</Label>
                  <Input 
                    required 
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="email@azienda.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input 
                    required 
                    type="password"
                    placeholder="Minimo 8 caratteri"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Contatto Principale
                </CardTitle>
                <CardDescription>Referente principale per le comunicazioni commerciali</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Nome e Cognome Referente *</Label>
                  <Input 
                    required 
                    value={formData.contact_name}
                    onChange={(e) => handleChange("contact_name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Referente</Label>
                  <Input 
                    type="email"
                    placeholder="referente@azienda.com"
                    value={formData.contact_email}
                    onChange={(e) => handleChange("contact_email", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono *</Label>
                  <Input 
                    required 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  Riferimenti Amministrativi
                </CardTitle>
                <CardDescription>Contatto per fatturazione e comunicazioni amministrative (se diverso dal referente principale)</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Nome e Cognome</Label>
                  <Input 
                    placeholder="Nome referente amministrativo"
                    value={formData.admin_contact_name}
                    onChange={(e) => handleChange("admin_contact_name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Amministrazione</Label>
                  <Input 
                    type="email"
                    placeholder="amministrazione@azienda.com"
                    value={formData.admin_contact_email}
                    onChange={(e) => handleChange("admin_contact_email", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input 
                    type="tel"
                    placeholder="Telefono ufficio amministrativo"
                    value={formData.admin_contact_phone}
                    onChange={(e) => handleChange("admin_contact_phone", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setStep(1)}>
                Indietro
              </Button>
              <Button type="button" onClick={() => handleNextStep(3)}>
                Avanti
              </Button>
            </div>
          </div>

          {/* Step 3: Operativo e Conferma */}
          <div className={step === 3 ? "block space-y-6" : "hidden"}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-accent" />
                  Dati Operativi & Spedizione
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2 pb-4 border-b">
                  <Checkbox 
                    id="same_address" 
                    checked={sameAsLegal}
                    onCheckedChange={(checked) => setSameAsLegal(checked)}
                  />
                  <Label htmlFor="same_address" className="cursor-pointer">
                    L'indirizzo di spedizione coincide con la sede legale
                  </Label>
                </div>

                {!sameAsLegal && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="md:col-span-2 space-y-2">
                      <Label>Indirizzo Spedizione</Label>
                      <Input 
                        value={formData.delivery_address}
                        onChange={(e) => handleChange("delivery_address", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Città</Label>
                      <Input 
                        value={formData.delivery_city}
                        onChange={(e) => handleChange("delivery_city", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CAP</Label>
                      <Input 
                        value={formData.delivery_postal_code}
                        onChange={(e) => handleChange("delivery_postal_code", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Referente al Magazzino</Label>
                      <Input 
                        value={formData.delivery_contact}
                        onChange={(e) => handleChange("delivery_contact", e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2 pt-2">
                  <Label>Note per corriere o orari</Label>
                  <Textarea 
                    placeholder="Es. Chiuso lunedì mattina, scarico con sponda..."
                    value={formData.delivery_notes}
                    onChange={(e) => handleChange("delivery_notes", e.target.value)}
                  />
                </div>
                
                <div className="space-y-2 pt-4">
                  <Label>Note Generali</Label>
                  <Textarea 
                    placeholder="Altre informazioni utili..."
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setStep(2)}>
                Indietro
              </Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={loading}>
                {loading ? "Invio in corso..." : "Conferma Registrazione"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
